// src/components/Dashboard/QuickAddFAB.js
// Speed-dial Floating Action Button — always visible on the dashboard.
// Keyboard shortcut: Ctrl+Shift+T (or Cmd+Shift+T on Mac) opens the expense dialog.

import React, { useState, useEffect, useCallback } from 'react';
import {
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  CircularProgress,
  Typography,
  Box,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SavingsIcon from '@mui/icons-material/Savings';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MicIcon from '@mui/icons-material/Mic';
import api from '../../services/api';
import QuickAddTransaction from './QuickAddTransaction';

// ─── Speed-dial action definitions ──────────────────────────────────────────

const ACTIONS = [
  {
    id: 'speak',
    icon: <MicIcon />,
    name: 'Speak',
    color: '#ef4444',
  },
  {
    id: 'expense',
    icon: <TrendingDownIcon />,
    name: 'Add Expense',
    color: '#ef4444',
  },
  {
    id: 'income',
    icon: <TrendingUpIcon />,
    name: 'Add Income',
    color: '#10b981',
  },
  {
    id: 'transfer',
    icon: <SwapHorizIcon />,
    name: 'Add Transfer',
    color: '#3b82f6',
  },
  {
    id: 'goal',
    icon: <SavingsIcon />,
    name: 'Goal Deposit',
    color: '#f59e0b',
  },
];

// ─── Goal Deposit Dialog ─────────────────────────────────────────────────────

const GoalDepositDialog = ({ open, onClose, onSuccess }) => {
  const [goals, setGoals] = useState([]);
  const [goalId, setGoalId] = useState('');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Load goals when dialog opens
  useEffect(() => {
    if (!open) return;
    setGoalId('');
    setAmount('');
    setFetching(true);
    api
      .get('/goals')
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : res.data?.goals || [];
        setGoals(list);
      })
      .catch((err) => {
        console.error('Failed to load goals:', err);
        setGoals([]);
      })
      .finally(() => setFetching(false));
  }, [open]);

  const handleSubmit = async () => {
    const amt = parseFloat(amount);
    if (!goalId) {
      setSnackbar({ open: true, message: 'Please select a goal', severity: 'error' });
      return;
    }
    if (!amount || isNaN(amt) || amt <= 0) {
      setSnackbar({ open: true, message: 'Please enter a valid amount', severity: 'error' });
      return;
    }

    try {
      setLoading(true);
      await api.post(`/goals/${goalId}/deposit`, { amount: amt });
      setSnackbar({ open: true, message: 'Goal deposit saved ✓', severity: 'success' });
      if (typeof onSuccess === 'function') onSuccess();
      onClose();
    } catch (err) {
      console.error('Goal deposit error:', err);
      const msg = err?.response?.data?.message || err?.message || 'Deposit failed';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, backgroundImage: 'none' } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Goal Deposit</DialogTitle>
        <DialogContent dividers sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {fetching ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          ) : goals.length === 0 ? (
            <Typography color="text.secondary">
              No savings goals found. Create a goal first.
            </Typography>
          ) : (
            <>
              <FormControl fullWidth size="small">
                <InputLabel id="goal-select-label">Select Goal</InputLabel>
                <Select
                  labelId="goal-select-label"
                  value={goalId}
                  onChange={(e) => setGoalId(e.target.value)}
                  label="Select Goal"
                >
                  {goals.map((g) => (
                    <MenuItem key={g._id || g.id} value={g._id || g.id}>
                      {g.name}
                      {g.targetAmount
                        ? ` — $${Number(g.currentAmount || 0).toFixed(0)} / $${Number(g.targetAmount).toFixed(0)}`
                        : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Deposit Amount"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                fullWidth
                size="small"
                inputProps={{ min: 0, step: '0.01' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSubmit();
                }}
                autoFocus
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={loading} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || fetching || goals.length === 0}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ backgroundColor: '#f59e0b', '&:hover': { backgroundColor: '#d97706' } }}
          >
            {loading ? 'Saving…' : 'Deposit'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

// ─── Main FAB Component ──────────────────────────────────────────────────────

/**
 * QuickAddFAB
 *
 * Props:
 *   onSuccess {function} — optional, called after any successful quick-add
 */
const QuickAddFAB = ({ onSuccess }) => {
  const [dialOpen, setDialOpen] = useState(false);

  // transactionDialog: null | 'expense' | 'income' | 'transfer'
  const [transactionDialog, setTransactionDialog] = useState(null);
  const [goalDialog, setGoalDialog] = useState(false);

  // Keyboard shortcut: Ctrl+Shift+T / Cmd+Shift+T → open expense dialog
  const handleKeyDown = useCallback((e) => {
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'T') {
      e.preventDefault();
      setTransactionDialog('expense');
      setDialOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleActionClick = (actionId) => {
    setDialOpen(false);
    if (actionId === 'speak') {
      window.dispatchEvent(new Event('sk:voice-start'));
    } else if (actionId === 'goal') {
      setGoalDialog(true);
    } else {
      setTransactionDialog(actionId);
    }
  };

  const handleTransactionClose = () => setTransactionDialog(null);
  const handleGoalClose = () => setGoalDialog(false);

  const handleSuccess = (data) => {
    if (typeof onSuccess === 'function') onSuccess(data);
  };

  return (
    <>
      {/* ── Speed-dial FAB ─────────────────────────────────────────────── */}
      <SpeedDial
        ariaLabel="Quick add actions"
        open={dialOpen}
        onOpen={() => setDialOpen(true)}
        onClose={() => setDialOpen(false)}
        direction="up"
        icon={
          <SpeedDialIcon
            icon={<AddIcon />}
            openIcon={<AddIcon sx={{ transform: 'rotate(45deg)' }} />}
          />
        }
        FabProps={{
          sx: {
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            boxShadow: '0 4px 24px rgba(16,185,129,0.45)',
            '&:hover': {
              background: 'linear-gradient(135deg, #34d399 0%, #10b981 100%)',
              boxShadow: '0 6px 32px rgba(16,185,129,0.6)',
            },
            transition: 'all 0.25s cubic-bezier(.4,0,.2,1)',
          },
        }}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1200,
        }}
      >
        {ACTIONS.map((action) => (
          <SpeedDialAction
            key={action.id}
            icon={action.icon}
            tooltipTitle={action.name}
            tooltipOpen
            onClick={() => handleActionClick(action.id)}
            FabProps={{
              sx: {
                backgroundColor: action.color,
                color: '#fff',
                '&:hover': {
                  backgroundColor: action.color,
                  filter: 'brightness(1.15)',
                },
              },
            }}
          />
        ))}
      </SpeedDial>

      {/* ── Transaction Dialog ──────────────────────────────────────────── */}
      <QuickAddTransaction
        open={Boolean(transactionDialog)}
        onClose={handleTransactionClose}
        initialType={transactionDialog || 'expense'}
        onSuccess={handleSuccess}
      />

      {/* ── Goal Deposit Dialog ─────────────────────────────────────────── */}
      <GoalDepositDialog
        open={goalDialog}
        onClose={handleGoalClose}
        onSuccess={handleSuccess}
      />
    </>
  );
};

export default QuickAddFAB;
